(ns dashboard.entity.question.profile
  "The principle contract of the dashboard.entity component namespaces are this:
   1. All components will NOT depend on keyword references to subs and events in
      external NSes, but rather receive data as arguments, functions and callbacks.
   2. All internal reliance on the re-frame app DB must be internally contained.
   3. All re-frame app subs and events must NOT be directly referenced by outside
      NSes, but rather via GETTER and SETTER functions - the same is true of internal
      calls - prefer using parameterized functions.
   4. The entity is a UI component that may encapsulate low-level business rules on
      UI controls, but is not responsible for hydrating components with data or entrenched
      references to component namespaces. The UI is customizable, and top-level entities
      should have configurable and interchangable UI elements."
  (:require ["react" :as react]
            [clojure.set :as set]
            [clojure.string :as str]
            [dashboard.common :as common :refer [expanded-text-container-style
                                                 wrapped-text-container-style]]
            [dashboard.components.answer-options :as answer-options]
            [dashboard.components.copy :as copy]
            [dashboard.components.questionnaire.notes-button :refer [notes-button]]
            [dashboard.entity.analysis.list :as analysis.list]
            [dashboard.entity.question.answer-scope :as answer-scope]
            [dashboard.entity.question.editable-answer-field :as edit.answer]
            [dashboard.entity.question.question-status :as question-status]
            [dashboard.html :as html]
            [io.repliance.answer-data :as answer-data]))

(defn question->response [question]
  (if-let [answer (:selected-answer question)]
    (answer-data/get-response (:source answer) (or answer (:answer-data question)))
    (or (when-not (str/blank? (-> question :answer-data :response))
          (-> question :answer-data :response))
        (-> question :answer-data :meta :faq :answer)
        (-> question :answer-data :meta :faq :response)
        (->> question
             :question-options
             (filterv :selected)
             first
             :content))))

(defn question->comment [question]
  (if-let [answer (:selected-answer question)]
    (answer-data/get-comment (:source answer) (or answer (:answer-data question)))
    (or (when-not (str/blank? (-> question :answer-data :additional-information))
          (-> question :answer-data :additional-information))
        (-> question :answer-data :meta :faq :additional-information)
        (-> question :answer-data :meta :faq :comment))))

(defn QuestionContent
  [{:keys [content suggestion suggestion-type]
    :tree/keys [depth]}]
  (when-not (= suggestion-type :analysis)
    [:div.questions--question-content
     {:class (when suggestion
               "ml-2")}
     (html/as-hiccup-with-anchors (if suggestion
                                    (:content suggestion)
                                    content))]))

(defn DisplayName [{:keys [display-name suggestion suggestion-type short-id target-questionnaire]}
                   ComparisonSymbol]
  [:div.questions--question-display-name
   (if suggestion
     [:div.ml-2
      [ComparisonSymbol
       (if (= :questionnaire suggestion-type)
         (if (:template target-questionnaire)
           "T"
           "Q")
         "A")
       (when (= :questionnaire suggestion-type)
         short-id)]]
     [:span display-name])])

(defn UnstructuredAnalysis
  [{:keys [selected-answer suggestion]}]
  (let [[overflow-hidden? set-overflow-hidden] (react/useState true)]
    (when-not suggestion
      [:div {:style (if overflow-hidden? wrapped-text-container-style expanded-text-container-style)
             :on-click #(set-overflow-hidden (not overflow-hidden?))}
       [:span
        (when-let [content (some-> selected-answer :analysis :content)]
          (html/as-hiccup-with-anchors content))]])))

(defn Status
  [{:keys [status selected-answer disabled suggestion]} on-update-status]
  (when-not suggestion
    [:div.questions--status
     [question-status/status-button {:status "approved"
                                     :current-status status
                                     :on-click (partial on-update-status "approved")
                                     :selected-answer selected-answer
                                     :disabled disabled}]
     [question-status/status-button {:status "in-review"
                                     :current-status status
                                     :on-click (partial on-update-status "in-review")
                                     :selected-answer selected-answer
                                     :disabled disabled}]
     [question-status/status-button {:status nil
                                     :current-status status
                                     :on-click (partial on-update-status nil)
                                     :selected-answer selected-answer
                                     :disabled disabled}]]))

(defn QuestionLabAndNotes
  [features-enabled on-show-question-lab-modal {:keys [disabled suggestion] :as question}]
  (when-not suggestion
    [:div
     (when (contains? features-enabled :applied-analysis-creation)
       [:a.button.is-secondary.is-small.is-rounded.mr-2
        (cond-> {:class (when disabled "is-disabled")
                 :title "Open question in Question Lab View"}
          (not disabled) (assoc :on-click on-show-question-lab-modal))
        [:span.icon>i.fas.fa-flask]])
     [notes-button question]]))

(defn Question
  "Question Profile for updating a question.
   All major components can be customized, but are expected. That is, it is left
   to the implementation to provide (constantly nil) for the various function
   components required and summoned.

   Accepts a map with keys:
   - features-enabled
   - answer-actions-ui
   - analysis-actions-ui
   - analysis-search-ui
   - comparison-symbol-ui
   - construct->categories
   - process-policy-pairs
   - on-analysis-action
   - on-commit-edit
   - on-click-construct
   - on-click-state
   - on-link-analysis
   - on-unlink-analysis
   - on-update-scope
   - on-update-status
   - on-report-analysis-suggestion
   - on-show-question-lab-modal"
  [{:keys [features-enabled
           answer-actions-ui
           analysis-actions-ui
           analysis-search-ui
           comparison-symbol-ui
           construct->categories
           process-policy-pairs
           on-analysis-action
           on-commit-edit
           on-click-construct
           on-click-state
           on-link-analysis
           on-unlink-analysis
           on-update-scope
           on-update-status
           on-scroll-to
           on-report-analysis-suggestion
           on-show-question-lab-modal]}
   question]
  (let [[ref set-ref] (react/useState nil)
        [edit-scope set-edit-scope] (react/useState false)]
    [:div.is-flex.my-0
     [:div.questions--question
      {:key (:id question)
       :ref set-ref}
      [:div.questions--question-title
       [DisplayName question comparison-symbol-ui]
       [:span.questions--question-content-clickable
        {:on-click #(on-scroll-to ref)}
        [QuestionContent question]]
       [Status question on-update-status]]

      [:div.questions--question-response.is-align-items-flex-start
       (when (and answer-actions-ui
                  (not (:suggestion question)))
         [:f> answer-actions-ui ref question])

       [:div.questions--question-response-editable-text
        [:div.level.is-flex.is-align-items-flex-start
         [:f> edit.answer/EditableAnswerField
          :response
          "response"
          question->response
          on-commit-edit
          question
          (not= "approved" (:status question))
          true]
         (when-not (:suggestion question)
          [copy/CopyToClipboard "Response" (question->response question)])
         (when-not (:suggestion question)
          [:div.ml-3.level.is-align-items-flex-start
          [:button.button.is-circular.is-small.is-primary.is-outlined.is-rounded.mr-1
           {:on-click #(when (or (str/blank? (question->response question))
                                 (js/confirm "Overwrite Answer?"))
                        (on-commit-edit question
                                       :response
                                       "Yes"))}
           "Y"]
          [:button.button.is-circular.is-small.is-danger.is-outlined.is-rounded.mr-1
           {:on-click #(when (or (str/blank? (question->response question))
                                 (js/confirm "Overwrite Answer?"))
                         (on-commit-edit question
                                         :response
                                         "No"))}
           "N"]
          [:button.button.is-circular.is-small.is-tertiary.is-rounded
           {:on-click #(when (or (str/blank? (question->response question))
                                 (js/confirm "Overwrite Answer?"))
                         (on-commit-edit question
                                         :response
                                         "N/A"))}
           "N/A"]])]
        (when-let [options (if (:selected-answer question)
                             (seq (:options (:selected-answer question)))
                                                     ;; If there is no answer yet, show options available from the question
                             (seq (:options question)))]
          [answer-options/AnswerOptions
           {:options options
            :on-change (fn [multi id]
                         (let [selected-option-ids (set (keep (fn [{:keys [id selected]}]
                                                                (when selected
                                                                  id))
                                                              options))
                               options (if multi
                                         (if (contains? selected-option-ids id)
                                           (disj selected-option-ids id)
                                           (conj selected-option-ids id))
                                         #{id})]
                           (on-commit-edit
                            question
                            :options
                            (map (fn [id]
                                   {:id id
                                    :selected true})
                                 options))))}])]

       (when-not (:suggestion question)
         [:div.questions--question-answer-scope
          (if-not edit-scope
            [:a {:on-click #(set-edit-scope true)}
             (-> question :answer-scope :content)
             [:i.icon.fas.fa-caret-down]]
            [:div.level-right
             [:f> answer-scope/AnswerScope
              {:on-commit (fn [& args]
                            (apply on-update-scope args)
                            (set-edit-scope false))
               :initial-answer-scope (-> question :answer-scope :content)
               :hide-title true}]
             [:button.button.is-tertiary
              {:on-click #(set-edit-scope false)}
              [:i.icon.fas.fa-close]]])])

       [:div.mr-3 [QuestionLabAndNotes features-enabled on-show-question-lab-modal question]]]

      [:div.columns.questions--answer-support
       (when analysis-actions-ui
         [:f> analysis-actions-ui question])

       [:div.column.pt-0.questions--answer-comment
        [:h6.title.mt-0.mb-1.is-7 "Comment"]
        [:div.level.is-flex
         (let [{:keys [selected-answer]} question
               comment-key (cond
                             (contains? selected-answer :additional-information) :additional-information
                             (contains? selected-answer :comment) :comment
                             :else :comment)]
           [:f> edit.answer/EditableAnswerField
            comment-key
            "comment"
            question->comment
            on-commit-edit
            question
            (not= "approved" (:status question))
            true])
         (when-not (:suggestion question)
          [copy/CopyToClipboard "Comment" (question->comment question)])]]

       [:div.column.pt-0
        [analysis.list/AnswerAppliedAnalyses analysis-search-ui "Operations (process)" question
         process-policy-pairs
         on-report-analysis-suggestion
         on-analysis-action
         on-click-construct
         on-click-state
         on-link-analysis
         on-unlink-analysis]]

       [:div.column.pt-0
        [analysis.list/AnswerAppliedAnalyses analysis-search-ui "Policy" question
         process-policy-pairs
         on-report-analysis-suggestion
         on-analysis-action
         on-click-construct
         on-click-state
         on-link-analysis
         on-unlink-analysis]]

       (when (->> question :selected-answer :applied-analyses
                  (filter
                   (fn [wut]
                     (not (seq (set/intersection (construct->categories wut)
                                                 #{"Policy" "Operations (process)"})))))
                  seq)
         [:div.column.pt-0
          [analysis.list/AnswerAppliedAnalyses analysis-search-ui
           (fn [category]
             (not (seq (set/intersection category #{"Policy" "Operations (process)"}))))
           question
           process-policy-pairs
           on-report-analysis-suggestion
           on-analysis-action
           on-click-construct
           on-click-state
           on-link-analysis
           on-unlink-analysis]])]]]))

(defn QuestionAsOption
  "Basic representation of a question used for list and content display for
  Analysis Search with a Question's context."
  [on-click question]
  [:div.questions--question.questions--question-selection
   {:on-click #(on-click (-> question
                             (assoc-in [:selected-answer :response]
                                       (question->response question))
                             (assoc-in [:selected-answer :additional-information]
                                       (question->comment question))))}
   [:div.questions--question-title
    [:div.questions--question-display-name "Q"]
    [:div.title.is-6.questions--question-content (:content question)]]
   [:div.level.my-0.questions--question-response.is-size-7.px-3
    [:div (or (:custom-name question) (:filename question))]
    [:div (str (common/format-date-time (:created question)))]]
   [:div.level.my-0.questions--answer-support.px-3.pb-2
    [:div
     [:div "A: " (str (question->response question))]
     (when-not (str/blank? (question->comment question))
      (into [:div [:hr.my-1] "Comment : "]
            (question->comment question)))]
    [:div (-> question :analyses count) " Analyses"]]])

(def question-wrapper :div.questions)

(defn QuestionProfile [on-click question]
  [question-wrapper
   [QuestionAsOption on-click question]])

(defn QuestionAnswerHistory
  "UI Wrapper for formatting question entries in the ASA Associated answer history."
  [on-click {:keys [answers
                    answer-content
                    content
                    display-name]}]
  (let [last-answer (first answers)]
    [question-wrapper
     [:div.questions--question.questions--question-selection
      {:on-click #(on-click last-answer)}
      [:div.questions--question-title
       [:div.questions--question-display-name display-name]
       [:div.title.is-6.questions--question-content content]]
      [:div.level.my-0.questions--answer-support.px-3.pb-2
       answer-content]]]))

(defn browser-extension-question
  [{:keys [content
           display-name]}
   on-scroll-to
   buttons
   & additional-content]
  [question-wrapper
   [:div.questions--question.questions--question-selection
    ;{:on-click #(on-click last-answer)}
    [:div.questions--question-title
     [:a.questions--question-display-name
      {:on-click #(on-scroll-to)}
      display-name]
     [:div.is-flex.is-flex-grow-1
      [:a.title.is-6.questions--question-content.is-flex-grow-1
       {:on-click #(on-scroll-to)}
       content]
      buttons]]
    [:div.level.my-0.questions--answer-support.px-3.pb-2
     (into [:<>] additional-content)]]])